import React, { useEffect, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { WizardStepWrapper } from '../step';
import {
  EmptyDemographics,
  HaveMRN,
  PatientDetails,
  PreviouslySeen,
  SchedulingDetails,
  VerifyPatientByMrnAndDobResult,
} from '../../../../api/models';
import { AreDemographicsValid } from '../../../demographicsInput';
import { ReCaptchaState } from '../../../reCaptchaValidator';
import { PatientInfo } from './patientInfo';
import { ContactPatient } from './contactPatient';
import { ContactPatientOption } from './ContactPatientOption';
import { VerifyPatient } from './verifyPatient';
import { DoVerify } from './doVerify';
import { LoadingSpinner, LoadingSpinnerSize } from '../../../../core';

export interface PatientStepPresenterProps {
  schedulingDetails: SchedulingDetails;
  reCaptchaRef: React.RefObject<ReCAPTCHA>;
  reCaptchaState: ReCaptchaState;
  onReCaptchaChange: () => void;
  validateReCaptcha?: () => Promise<boolean>;
  onChangePatientDetails: (patientDetails: PatientDetails) => void;
  validateMrnAndDob?: () => Promise<VerifyPatientByMrnAndDobResult | undefined>;
  sendAccessCode?: (
    mrn: string,
    dateOfBirth: string,
    contactType: string,
    phoneKey: string
  ) => Promise<boolean | undefined>;
  verifyAccessCode?: (
    mrn: string,
    dateOfBirth: string,
    contactType: string,
    patientAccessCode: string
  ) => Promise<boolean | undefined>;
  onContinue: () => void;
}

const DefaultPatientDetails: PatientDetails = {
  target: undefined,
  demographics: EmptyDemographics,
  previouslySeen: undefined,
  haveMRN: undefined,
};

enum PatientStepState {
  Initial = 'Initial',
  Contact = 'Contact',
  Verify = 'Verify',
  DoVerify = 'DoVerify',
}

const getContinueButtonText = (state: PatientStepState): string | undefined => {
  if (state === PatientStepState.Contact) {
    return 'Send Code';
  }

  if (state === PatientStepState.Verify) {
    return 'Verify Code and Continue';
  }

  return undefined;
};

const PatientStepPresenter: React.FC<PatientStepPresenterProps> = (
  props: PatientStepPresenterProps
) => {
  const [state, setState] = useState<PatientStepState>(
    PatientStepState.Initial
  );
  const [patientDetails, setPatientDetails] = useState<PatientDetails>(
    DefaultPatientDetails
  );
  const [contactPatientOption, setContactPatientOption] =
    useState<ContactPatientOption>({});
  const [patientAccessCode, setPatientAccessCode] = useState<string>('');
  const [hideContinue, setHideContinue] = useState<boolean>(false);
  const [validate, setValidate] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [showLoading, setShowLoading] = useState<boolean>(false);

  useEffect(() => {
    setPatientDetails({
      ...patientDetails,
      target: props.schedulingDetails.patientDetails?.target,
      demographics:
        props.schedulingDetails.patientDetails?.demographics ??
        EmptyDemographics,
      previouslySeen: props.schedulingDetails.patientDetails?.previouslySeen,
      haveMRN: props.schedulingDetails.patientDetails?.haveMRN,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.schedulingDetails.patientDetails]);

  const onChangePatientDetails = (newPatientDetails: PatientDetails) => {
    setPatientDetails(newPatientDetails);
    setValidate(false);
    props.onChangePatientDetails(newPatientDetails);
  };

  const onChangeContactOption = (contactOption: ContactPatientOption) => {
    setContactPatientOption(contactOption);
  };

  const onChangePatientAccessCode = (value: string) => {
    setPatientAccessCode(value);
  };

  const onTryAnotherMethod = () => {
    setShowError(false);
    setState(PatientStepState.Contact);
  };

  const onContinue = async () => {
    setShowError(false);

    if (state === PatientStepState.Initial) {
      await continueInitialStep();
    }

    if (state === PatientStepState.Contact) {
      continueContactStep();
    }

    if (state === PatientStepState.Verify) {
      continueVerifyStep();
    }

    if (state === PatientStepState.DoVerify) {
      continueDoVerifyStep();
    }

    setShowLoading(false);
  };

  const continueInitialStep = async () => {
    if (patientDetails?.target === undefined) {
      // haven't answered question
      setValidate(true);
      setState(PatientStepState.Initial);
      return;
    }

    if (props.schedulingDetails.patientDetails?.previouslySeen === undefined) {
      // haven't answered question
      setValidate(true);
      setState(PatientStepState.Initial);
      return;
    }

    if (
      props.schedulingDetails.patientDetails?.previouslySeen ===
        PreviouslySeen.Yes &&
      props.schedulingDetails.patientDetails.haveMRN === undefined
    ) {
      // haven't answered question
      setValidate(true);
      setState(PatientStepState.Initial);
      return;
    }

    if (
      props.schedulingDetails.patientDetails?.previouslySeen ===
        PreviouslySeen.Yes &&
      props.schedulingDetails.patientDetails.haveMRN === HaveMRN.Yes
    ) {
      setShowLoading(true);
      // we are attempting to confirm a specific patient
      const demographics = props.schedulingDetails.patientDetails.demographics;
      if (
        demographics?.mrn &&
        demographics.mrn.length > 0 &&
        demographics.dateOfBirth &&
        demographics.dateOfBirth !== null &&
        (props.validateReCaptcha === undefined ||
          (await props.validateReCaptcha()))
      ) {
        // everything is valid, move on to the confirmation step
        setValidate(false);
        setState(PatientStepState.Contact);
        setShowLoading(false);
        return;
      }

      setValidate(true);
      setState(PatientStepState.Initial);
      setShowLoading(false);
      return;
    }

    setShowLoading(true);
    if (
      areDemographicsValid() &&
      (props.validateReCaptcha === undefined ||
        (await props.validateReCaptcha()))
    ) {
      // no mrn confirmation
      props.onContinue();
      setValidate(false);
      setState(PatientStepState.Initial);
      setShowLoading(false);
      return;
    }

    setValidate(true);
    setState(PatientStepState.Initial);
    setShowLoading(false);
  };

  const continueContactStep = async () => {
    if (
      contactPatientOption.contactType === undefined ||
      contactPatientOption.contactType.length === 0
    ) {
      // contact type not chosen
      setValidate(true);
      setState(PatientStepState.Contact);
      return;
    }

    if (
      contactPatientOption.phoneKey === undefined ||
      contactPatientOption.phoneKey.length === 0
    ) {
      // phone not chosen
      setValidate(true);
      setState(PatientStepState.Contact);
      return;
    }

    if (props.sendAccessCode) {
      const result = await props.sendAccessCode(
        patientDetails.demographics?.mrn ?? '',
        patientDetails.demographics?.dateOfBirth ?? '',
        contactPatientOption.contactType ?? '',
        contactPatientOption.phoneKey ?? ''
      );

      if (result === undefined || result === false) {
        setShowError(true);
        return;
      }
    }

    setValidate(false);
    setPatientAccessCode('');
    setState(PatientStepState.Verify);
    return;
  };

  const continueVerifyStep = () => {
    if (patientAccessCode.length === 0) {
      setValidate(true);
      setState(PatientStepState.Verify);
      return;
    }

    setValidate(false);
    setState(PatientStepState.DoVerify);
    return;
  };

  const continueDoVerifyStep = () => {
    props.onContinue();
    setValidate(false);
    setState(PatientStepState.Initial);
    return;
  };

  // return false to cancel onContinue
  const areDemographicsValid = (): boolean => {
    if (patientDetails.demographics) {
      return AreDemographicsValid(patientDetails.demographics);
    }
    return false;
  };

  const onDoVerifySuccess = () => {
    onContinue();
  };

  const validateMrnAndDob = async (): Promise<
    VerifyPatientByMrnAndDobResult | undefined
  > => {
    if (props.validateMrnAndDob) {
      setHideContinue(true);
      const response = await props.validateMrnAndDob();
      setHideContinue(false);
      return response;
    }
    return undefined;
  };

  const verifyAccessCode = async (
    mrn: string,
    dateOfBirth: string,
    contactType: string,
    patientAccessCode: string
  ): Promise<boolean | undefined> => {
    if (props.verifyAccessCode) {
      setHideContinue(true);
      const response = await props.verifyAccessCode(
        mrn,
        dateOfBirth,
        contactType,
        patientAccessCode
      );
      setHideContinue(false);
      return response;
    }
    return undefined;
  };

  const sendAccessCode = async (
    mrn: string,
    dateOfBirth: string,
    contactType: string,
    phoneKey: string
  ): Promise<boolean | undefined> => {
    if (props.sendAccessCode) {
      setHideContinue(true);
      const response = await props.sendAccessCode(
        mrn,
        dateOfBirth,
        contactType,
        phoneKey
      );
      setHideContinue(false);
      return response;
    }
    return undefined;
  };

  const onError = () => {
    setShowError(true);

    if (state === PatientStepState.Contact) {
      setState(PatientStepState.Initial);
    } else if (state === PatientStepState.Verify) {
      setState(PatientStepState.Contact);
    } else if (state === PatientStepState.DoVerify) {
      setState(PatientStepState.Verify);
    }
  };

  return (
    <WizardStepWrapper
      onContinue={
        state === PatientStepState.DoVerify ||
        showLoading ||
        hideContinue ||
        patientDetails.target === undefined
          ? undefined
          : onContinue
      }
      specialFooter={
        showLoading ? (
          <LoadingSpinner
            label="Loading, please wait..."
            size={LoadingSpinnerSize.Medium}
            delayInMs={0}
          />
        ) : undefined
      }
      continueText={getContinueButtonText(state)}
      stickyWhenNarrow={false}
    >
      {state === PatientStepState.Contact ? (
        <ContactPatient
          validate={validate}
          patientDetails={patientDetails}
          contactPatientOption={contactPatientOption}
          showError={showError}
          validateMrnAndDob={validateMrnAndDob}
          onChangeContactOption={onChangeContactOption}
          onError={onError}
        />
      ) : state === PatientStepState.Verify ? (
        <VerifyPatient
          validate={validate}
          contactInfo={{
            mrn: patientDetails.demographics?.mrn ?? '',
            dateOfBirth: patientDetails.demographics?.dateOfBirth ?? '',
            contactType: contactPatientOption.contactType ?? '',
            phoneKey: contactPatientOption.phoneKey ?? '',
            phoneString: contactPatientOption.phoneString ?? '',
          }}
          showError={showError}
          patientAccessCode={patientAccessCode}
          onChangePatientAccessCode={onChangePatientAccessCode}
          onTryAnotherMethod={onTryAnotherMethod}
          sendAccessCode={sendAccessCode}
          onError={onError}
        />
      ) : state === PatientStepState.DoVerify ? (
        <DoVerify
          contactInfo={{
            mrn: patientDetails.demographics?.mrn ?? '',
            dateOfBirth: patientDetails.demographics?.dateOfBirth ?? '',
            contactType: contactPatientOption.contactType ?? '',
            patientAccessCode: patientAccessCode ?? '',
          }}
          verifyAccessCode={verifyAccessCode}
          onSuccess={onDoVerifySuccess}
          onError={onError}
        />
      ) : (
        <PatientInfo
          validate={validate}
          patientDetails={patientDetails}
          showError={showError}
          onChangePatientDetails={onChangePatientDetails}
          reCaptchaRef={props.reCaptchaRef}
          reCaptchaState={props.reCaptchaState}
          onReCaptchaChange={props.onReCaptchaChange}
        />
      )}
    </WizardStepWrapper>
  );
};

export default PatientStepPresenter;
